import React, { useCallback, useEffect, useRef, useState } from "react";
import { IoClose, IoRefreshOutline, IoSearchOutline } from "react-icons/io5";
import { jobStatusBadge } from "../data/jobStatusBadge";
import { geoTypeLabel } from "../../config/products";
import { jobsApi, Job } from "../../services/jobsApi";
import { describeRequestError } from "../../services/http";
import { formatJobTime, jobAreaSummary, jobCreatedText, jobParts, jobTitle, jobWindowText } from "../../utils/jobTitle";
import Spinner from "../common/Spinner";

const CloseIcon = IoClose as any;
const RefreshIcon = IoRefreshOutline as any;
const SearchIcon = IoSearchOutline as any;

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const badge = jobStatusBadge(status);
  return <span className={`badge badge-sm ${badge.className}`}>{badge.text}</span>;
};

const JobsList: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  // `loading` is only the first load, when there is nothing to show yet; `refreshing` is every load.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const id = ++latestRequest.current;
    setRefreshing(true);
    setError(null);
    try {
      const fetched = await jobsApi.getAllJobs();
      if (id === latestRequest.current) setJobs(fetched);
    } catch (e) {
      if (id === latestRequest.current) setError(describeRequestError(e));
    } finally {
      if (id === latestRequest.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    load();
    // An answer that arrives after the list has gone is dropped.
    return () => {
      latestRequest.current += 1;
    };
  }, [load]);

  const filtered = jobs.filter((j) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      jobTitle(j).toLowerCase().includes(q) ||
      j.job_id.toLowerCase().includes(q) ||
      j.status.toLowerCase().includes(q) ||
      j.agg_type?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="py-20">
        <Spinner size="lg" label="Loading jobs…" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-ink-faint mb-3">
            Activity
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-ink mb-3">
            Your Jobs
          </h1>
          <p className="text-lg text-ink-muted">
            {jobs.length === 0
              ? "Submit some analytics jobs to see them here."
              : `${jobs.length} job${jobs.length === 1 ? "" : "s"} submitted.`}
          </p>
        </div>

        <div className="flex w-full items-center gap-2 sm:w-auto">
          {jobs.length > 0 && (
            <div className="relative w-full sm:w-72">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-faint" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search jobs…"
                aria-label="Search jobs"
                className="w-full rounded-full border border-paper-300 bg-surface pl-9 pr-4 py-2 text-sm placeholder:text-ink-faint focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 transition"
              />
            </div>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm shrink-0"
            onClick={load}
            disabled={refreshing}
          >
            <RefreshIcon className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {error !== null && (
        <div role="alert" className="alert alert-error mb-6 text-sm">
          <div>
            <p className="font-bold">Could not load your jobs.</p>
            <p>{error}</p>
          </div>
          <button type="button" className="btn btn-sm" onClick={load} disabled={refreshing}>
            Try again
          </button>
        </div>
      )}

      {jobs.length === 0 ? (
        error === null && (
          <div className="rounded-3xl border border-dashed border-paper-300 bg-surface py-16 text-center">
            <p className="text-base text-ink-muted">No jobs found.</p>
            <p className="mt-1 text-sm text-ink-faint">
              Submit some analytics jobs to see them here.
            </p>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((job) => (
            <button
              key={job.job_id}
              onClick={() => setSelectedJob(job)}
              className="group text-left rounded-2xl border border-paper-300/70 bg-surface p-5 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-card hover:border-brand-500/30"
            >
              <div className="mb-3">
                <StatusBadge status={job.status} />
              </div>
              <p className="font-display text-base font-semibold text-ink mb-1">
                {jobTitle(job)}
              </p>
              <p className="text-sm text-ink-muted">
                {[jobAreaSummary(job), jobWindowText(job)].filter(Boolean).join(" · ")}
              </p>
              <p className="mt-2 font-mono text-xs text-ink-subtle truncate">
                {job.job_id}
              </p>
              <p className="mt-3 text-xs text-ink-faint">{jobCreatedText(job.created_at)}</p>
            </button>
          ))}
        </div>
      )}

      {jobs.length > 0 && (
        <p className="mt-6 text-xs text-ink-muted">
          A job's status updates while a page is waiting for it, so a job you left early can still show
          Queued or Processing.
        </p>
      )}

      {/* Job Details Modal */}
      {selectedJob && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4 animate-fade-in"
          onClick={() => setSelectedJob(null)}
        >
          <div
            className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-3xl bg-surface shadow-card-hover tf-scroll"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-paper-300/70 bg-surface px-6 py-4">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                  Job Details
                </p>
                <h3 className="font-display text-xl font-bold text-ink mt-0.5">
                  {jobTitle(selectedJob)}
                </h3>
              </div>
              <button
                onClick={() => setSelectedJob(null)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink-muted hover:bg-paper-200 transition-colors"
                aria-label="Close"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="flex items-center justify-between rounded-2xl border border-paper-300/70 bg-paper-100 px-4 py-3">
                <span className="text-sm font-medium text-ink-muted">Status</span>
                <StatusBadge status={selectedJob.status} />
              </div>

              <DetailRow label="Job ID" value={selectedJob.job_id} mono />
              <DetailRow label="Product" value={jobParts(selectedJob).product ?? selectedJob.agg_type} />
              <DetailRow label="Measure" value={jobParts(selectedJob).measure ?? selectedJob.measure ?? ""} />
              <DetailRow
                label="Time resolution"
                value={jobParts(selectedJob).resolution ?? selectedJob.activity_type ?? ""}
              />
              <DetailRow label="Window" value={jobWindowText(selectedJob)} />
              <DetailRow label="Areas" value={jobAreaSummary(selectedJob)} />

              {selectedJob.geo_type && (
                <DetailRow label="Area type" value={geoTypeLabel(selectedJob.geo_type)} />
              )}
              {selectedJob.geo_ids && selectedJob.geo_ids.length > 0 && (
                <DetailRow label="Area IDs" value={selectedJob.geo_ids.join(", ")} mono />
              )}
              {selectedJob.origin_geo_type && (
                <DetailRow label="Origin area type" value={geoTypeLabel(selectedJob.origin_geo_type)} />
              )}
              {selectedJob.origin_geo_ids && selectedJob.origin_geo_ids.length > 0 && (
                <DetailRow label="Origin IDs" value={selectedJob.origin_geo_ids.join(", ")} mono />
              )}
              {selectedJob.destination_geo_type && (
                <DetailRow label="Destination area type" value={geoTypeLabel(selectedJob.destination_geo_type)} />
              )}
              {selectedJob.destination_geo_ids && selectedJob.destination_geo_ids.length > 0 && (
                <DetailRow label="Destination IDs" value={selectedJob.destination_geo_ids.join(", ")} mono />
              )}

              <DetailRow label="Created" value={formatJobTime(selectedJob.created_at)} />
              <DetailRow label="Updated" value={formatJobTime(selectedJob.updated_at)} />
            </div>

            <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-paper-300/70 bg-surface px-6 py-4">
              {selectedJob.full && <span className="mr-auto text-sm text-ink-muted">Full export (API)</span>}
              <button
                onClick={() => setSelectedJob(null)}
                className="inline-flex items-center rounded-full border border-paper-300 px-4 py-2 text-sm font-medium text-ink-muted hover:bg-paper-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface DetailRowProps {
  label: string;
  value: string;
  mono?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, mono }) => (
  <div className="grid grid-cols-3 gap-3 py-2 border-b border-paper-200 last:border-b-0">
    <dt className="text-sm font-medium text-ink-muted">{label}</dt>
    <dd className={`col-span-2 text-sm text-ink-soft break-all ${mono ? "font-mono" : ""}`}>
      {value}
    </dd>
  </div>
);

export default JobsList;
